import { useCallback, useEffect, useRef, useState } from 'react';

import { getSettings, updateSettings } from '../../../api/settingsApi';
import type { NearMissConfig, RuntimeInventory } from '../../../api/nearMiss';
import { normalizeNearMiss, sameNearMiss } from '../../nearMiss/nearMissModel';
import { ApiError, AuthError } from '../../../utils/apiClient';
import { formatApiError } from '../../../utils/formatApiError';

export interface SettingsData {
  nearMiss?: NearMissConfig;
  runtimeInventory?: RuntimeInventory | null;
  activeMetricsProvider?: 'prometheus' | 'dynatrace';
  projectName: string;
  testedSystemUrl: string;
  environment: string;
  thresholds: Record<string, number>;
  prometheusUrl: string;
  inheritedPrometheusUrl?: string;
  prometheusProfileConfigured?: boolean;
  prometheusConfigSource?: string;
  prometheusProfileReady?: boolean;
  prometheusDiscoveredJobs?: string[];
  prometheusValidJobs?: string[];
  prometheusSelectedJob?: string | null;
  prometheusJobAmbiguous?: boolean;
  prometheusJobValidationError?: string | null;
  queryRange: string;
  customQueries: {
    errorRate: string;
    latency: string;
    cpu: string;
    memory: string;
  };
  testLibraryEnabled: boolean;
  historicalDataEnabled: boolean;
  observabilityEnabled: boolean;
  experimentLibrary: any[];
  notificationConfig: any;
  versions: any[];
  canEditSettings: boolean;
}

const applySettingsDefaults = (data: SettingsData): SettingsData => ({
  ...data,
  nearMiss: normalizeNearMiss(data.nearMiss),
  environment: data.environment || 'development',
  customQueries: data.customQueries || {
    errorRate: 'rate(http_requests_total{status=~"5.."}[5m])',
    latency: 'histogram_quantile(0.95, http_request_duration_seconds)',
    cpu: 'avg(cpu_usage_percent)',
    memory: 'avg(memory_usage_bytes)',
  },
  testLibraryEnabled: data.testLibraryEnabled !== false,
  historicalDataEnabled: data.historicalDataEnabled !== false,
  observabilityEnabled: data.observabilityEnabled !== false,
  inheritedPrometheusUrl: data.inheritedPrometheusUrl || '',
  prometheusProfileConfigured: Boolean(data.prometheusProfileConfigured),
  prometheusConfigSource: data.prometheusConfigSource || 'none',
  prometheusProfileReady: Boolean(data.prometheusProfileReady),
  prometheusDiscoveredJobs: Array.isArray(data.prometheusDiscoveredJobs)
    ? data.prometheusDiscoveredJobs
    : [],
  prometheusValidJobs: Array.isArray(data.prometheusValidJobs)
    ? data.prometheusValidJobs
    : [],
  prometheusSelectedJob: data.prometheusSelectedJob || null,
  prometheusJobAmbiguous: Boolean(data.prometheusJobAmbiguous),
  prometheusJobValidationError: data.prometheusJobValidationError || null,
  canEditSettings: Boolean(data.canEditSettings),
});

export const useProjectSettings = (projectId?: string) => {
  const scope = useRef({ projectId, generation: 0 });
  if (scope.current.projectId !== projectId) scope.current = { projectId, generation: scope.current.generation + 1 };
  const mounted = useRef(false);
  const reads = useRef(0);
  const saving = useRef<number | null>(null);
  const [loadedProject, setLoadedProject] = useState<string>();
  const current = (generation: number) => mounted.current && scope.current.generation === generation;
  const [settings, setSettings] = useState<SettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    const generation = scope.current.generation;
    const read = ++reads.current;
    if (!projectId) {
      setLoading(false);
      setSettings(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getSettings<SettingsData>(projectId);
      if (!current(generation) || reads.current !== read) return;
      setLoadedProject(projectId);
      setSettings(applySettingsDefaults(data));
    } catch (err: any) {
      if (!current(generation) || reads.current !== read) return;
      setLoadedProject(projectId);
      if (err instanceof AuthError) return;
      if (err instanceof ApiError && err.status === 403) {
        setError(formatApiError(err, 'You do not have permission to view project settings.'));
        return;
      }
      setError(formatApiError(err, 'Failed to load settings'));
    } finally {
      if (current(generation) && reads.current === read) setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    const readCounter = reads;
    mounted.current = true;
    setError(null); setSuccess(null); setSettings(null); setLoadedProject(undefined);
    void fetchSettings();
    return () => { mounted.current = false; ++readCounter.current; };
  }, [fetchSettings]);

  const saveSettings = async (updates: Partial<SettingsData>) => {
    if (!settings || !projectId || loadedProject !== projectId) {
      throw new Error('Select a project and load its settings before saving.');
    }

    const generation = scope.current.generation;
    if (saving.current === generation) throw new Error('Settings save already in progress.');
    saving.current = generation;
    ++reads.current;
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const payload = {
        testedSystemUrl: settings.testedSystemUrl,
        environment: settings.environment,
        thresholds: settings.thresholds,
        queryRange: settings.queryRange,
        customQueries: settings.customQueries,
        testLibraryEnabled: settings.testLibraryEnabled,
        historicalDataEnabled: settings.historicalDataEnabled,
        experimentLibrary: Array.isArray(settings.experimentLibrary)
          ? settings.experimentLibrary
          : [],
        notificationConfig:
          typeof settings.notificationConfig === 'object'
            ? settings.notificationConfig
            : {},
        versions: Array.isArray(settings.versions) ? settings.versions : [],
        ...updates,
      };

      await updateSettings(projectId, payload);
      // PUT returns an acknowledgement, not SettingsData. Confirm the saved
      // configuration through the same GET used when the page reloads.
      const data = await getSettings<SettingsData>(projectId);
      if (!current(generation)) throw new Error('Project changed before save verification completed.');
      if (updates.nearMiss && (!data.nearMiss || !sameNearMiss(updates.nearMiss, data.nearMiss))) {
        throw new Error('Near-miss save/readback did not match. Reload settings and verify the configuration before evaluating.');
      }
      if (updates.prometheusUrl?.trim() && !data.prometheusUrl?.trim()) {
        throw new Error('The saved project Prometheus URL was not returned. Reload settings and verify the configuration.');
      }
      if (updates.prometheusUrl === '' && (data.prometheusUrl || data.inheritedPrometheusUrl)) {
        throw new Error('Project connection removal could not be verified. Reload settings.');
      }
      setSettings(applySettingsDefaults(data));
      setSuccess('Settings saved successfully');
    } catch (err: any) {
      if (!current(generation)) throw err;
      if (err instanceof AuthError) throw err;
      if (err instanceof ApiError && err.status === 403) {
        setError(formatApiError(err, 'You do not have permission to update project settings.'));
        throw err;
      }
      setError(formatApiError(err, 'Failed to save settings'));
      throw err;
    } finally {
      if (saving.current === generation) saving.current = null;
      if (current(generation)) setLoading(false);
    }
  };

  return {
    settings: loadedProject === projectId ? settings : null,
    setSettings,
    loading: loadedProject !== projectId && !!projectId ? true : loading,
    error,
    success,
    setSuccess,
    fetchSettings,
    saveSettings,
  };
};
